/*
 * Mobinova — barre « livraison offerte » du panier.
 * À chaque rendu (ouverture du tiroir, ajout ou retrait d'un article), la barre part de sa valeur
 * précédente, le montant restant défile jusqu'à sa nouvelle valeur et l'objectif atteint est salué.
 */

const STORE_KEY = 'mn-free-shipping';
const DURATION = 900;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readPrevious = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key)) || null;
  } catch {
    return null;
  }
};

const savePrevious = (key, state) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {}
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/* Reprend le format monétaire de la boutique, ex. « €{{amount_with_comma_separator}} ». */
const formatMoney = (cents, format) => {
  const value = Math.max(0, cents) / 100;
  const withComma = value.toFixed(2).replace('.', ',');
  const replacements = {
    amount: value.toFixed(2),
    amount_no_decimals: Math.round(value).toString(),
    amount_with_comma_separator: withComma,
    amount_no_decimals_with_comma_separator: Math.round(value).toString(),
    amount_with_apostrophe_separator: value.toFixed(2),
  };
  return (format || '{{amount_with_comma_separator}} €').replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match, key) => replacements[key] ?? withComma
  );
};

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    // Dans le tiroir fermé, on attend son ouverture pour que l'animation soit visible.
    const drawer = this.closest('cart-drawer');
    if (drawer && !drawer.classList.contains('is-open')) {
      this.observer = new MutationObserver(() => {
        if (!drawer.classList.contains('is-open')) return;
        this.observer.disconnect();
        this.play();
      });
      this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      return;
    }
    this.play();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play() {
    const key = `${STORE_KEY}:${this.closest('cart-drawer') ? 'drawer' : 'page'}`;
    const progress = parseFloat(this.dataset.progress) || 0;
    const missing = parseInt(this.dataset.missing, 10) || 0;
    const complete = this.classList.contains('is-complete');
    const previous = readPrevious(key);
    savePrevious(key, { progress, missing });

    const bar = this.querySelector('.cart-progress-bar__progress_1');
    const amount = this.querySelector('[data-mn-fs-amount]');
    if (reduceMotion || !bar) return;

    const fromProgress = previous ? Math.min(previous.progress, 100) : 0;
    const fromMissing = previous && !complete ? previous.missing : missing;

    // L'objectif vient d'être atteint : petite célébration.
    if (complete && previous && previous.progress < 100) {
      this.classList.add('is-celebrating');
      setTimeout(() => this.classList.remove('is-celebrating'), 1600);
    }

    // Le montant restant a baissé : on le fait ressortir.
    if (amount && previous && missing < previous.missing) {
      amount.classList.add('is-bumping');
      setTimeout(() => amount.classList.remove('is-bumping'), 700);
    }

    // Sans changement (simple ouverture du tiroir) : un reflet discret sur la barre.
    if (previous && Math.abs(previous.progress - progress) < 0.01) {
      this.classList.add('is-shining');
      setTimeout(() => this.classList.remove('is-shining'), 1400);
      return;
    }

    bar.value = fromProgress;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / DURATION, 1);
      const eased = easeOutCubic(t);
      bar.value = fromProgress + (progress - fromProgress) * eased;
      if (amount && fromMissing !== missing) {
        amount.textContent = formatMoney(
          Math.round(fromMissing + (missing - fromMissing) * eased),
          this.dataset.moneyFormat
        );
      }
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('mn-free-shipping')) {
  customElements.define('mn-free-shipping', FreeShippingBar);
}
